import type { StatusTone } from "@/components/ui/status-badge";

import { CaseStatus } from "../../api/types/case";

export type CaseStatusColor = {
  /** Tinted background for a filled pill. */
  bg: string;
  /** Ink for text and the status dot. */
  color: string;
  hoverBg: string;
};

type Tone = Extract<
  StatusTone,
  "info" | "warn" | "purple" | "success" | "danger" | "neutral"
>;

/** The one status-to-tone map: `CaseStatusPill`, `StatusTag` and the IDE chips read it. */
export const CASE_STATUS_TONE: Record<CaseStatus, Tone> = {
  [CaseStatus.IN_PROGRESS]: "info",
  [CaseStatus.ON_HOLD]: "warn",
  [CaseStatus.FILED]: "purple",
  [CaseStatus.DECISION]: "purple",
  [CaseStatus.APPROVED]: "success",
  [CaseStatus.DENIED]: "danger",
  [CaseStatus.REOPENED]: "info",
  [CaseStatus.CLOSED]: "neutral"
};

// The runtime tokens, not `--color-*`: the `@theme inline` block never emits those.
const colorsFor = (tone: Tone): CaseStatusColor =>
  tone === "neutral"
    ? {
        bg: "var(--fill-2)",
        color: "var(--fg-4)",
        hoverBg: "var(--fill-3)"
      }
    : {
        bg: `var(--${tone}-soft)`,
        color: `var(--${tone})`,
        hoverBg: `color-mix(in srgb, var(--${tone}-soft), var(--${tone}) 8%)`
      };

/** Ink/paper pairs for each case status, as theme-aware CSS colors. */
export const STATUS_COLORS = Object.fromEntries(
  Object.entries(CASE_STATUS_TONE).map(([status, tone]) => [
    status,
    colorsFor(tone)
  ])
) as Record<CaseStatus, CaseStatusColor>;

export const statusColor = (status?: CaseStatus | null): CaseStatusColor =>
  STATUS_COLORS[status || CaseStatus.IN_PROGRESS] ??
  STATUS_COLORS[CaseStatus.IN_PROGRESS];
